// Shared Customer.io CDP browser client.
//
// TWO consumers need the SAME AnalyticsBrowser instance:
//   - `providers/customerio-provider.tsx` (React) — page views, the
//     identify/reset lifecycle, and the in-app messaging plugin.
//   - `lib/analytics/providers/customerio` (analytics sink) — mirrors every
//     typed product event via the composite fan-out.
//
// Loading the SDK twice would run two flush queues against one write key, and
// a mid-session identify on one instance would not update the other — events
// from the second instance would keep flowing under the pre-login identity.
// So the client is created exactly once here and handed to both consumers.

import { AnalyticsBrowser } from '@customerio/cdp-analytics-browser';

let client: AnalyticsBrowser | null = null;

/**
 * Networks whose traffic may reach Customer.io.
 *
 * Customer.io is the production CRM, not an analytics warehouse: anything
 * landing there can mint a profile, trigger a campaign, and consume billed
 * profile quota. Only the deployed networks qualify — an engineer's local
 * session was writing `network: "local"` events into the live workspace
 * alongside real ones.
 *
 * An allowlist rather than a denylist, so a future non-production network is
 * excluded by default instead of by remembering to add it here. Typed on
 * `string` so callers can pass an event property without a cast.
 */
const CUSTOMERIO_NETWORKS: readonly string[] = ['devnet', 'mainnet'];

export function isCustomerIoNetwork(network: string | undefined): boolean {
  return network !== undefined && CUSTOMERIO_NETWORKS.includes(network);
}

/**
 * Create (once) and return the shared CDP client, or null when this session
 * must not reach Customer.io.
 *
 * Loading is itself a write — the SDK fires an automatic page call as soon as
 * it loads — so gating the downstream page/identify/track calls would not be
 * enough. On a non-production network the client must never exist at all.
 *
 * `network` is passed in rather than read from the store here, so both callers
 * pass the network THEY are acting on: the React provider passes the one it
 * renders for, the event sink passes the one stamped on the event.
 */
export function loadCustomerIoCdp(writeKey: string, network: string): AnalyticsBrowser | null {
  if (!isCustomerIoNetwork(network)) return null;
  if (client === null) {
    client = AnalyticsBrowser.load({ writeKey });
  }
  return client;
}
